import { Collapse, Table, TableBody, TableRow } from '@mui/material';
import { useUpdateProofreadEventApi } from 'api/mypage/proofread_event';
import { DisplayAttachmentsContainer } from 'components/shared/display_attachments_container';
import { Flex } from 'components/shared/flex';
import {
  CheckBoxField,
  FilesInput,
  MonthField,
  NumberField,
  SelectField,
  StringField,
} from 'components/shared/form/input';
import Modal, { ModalSet } from 'components/shared/modal';
import { ExTableCell } from 'components/shared/table';
import ThemedButton from 'components/shared/themed_button';
import { GlobalStateContext } from 'contexts/global_state_context';
import { MypageStateContext } from 'contexts/mypage_state_context';
import { ProofreadEvent, ProofreadEventForm } from 'entities/proofread_event';
import moment from 'moment';
import { useForm } from 'rac';
import React, { useContext, useState } from 'react';
import { useEffectSkipFirst } from 'utils/hooks';
import { ProofreadApproverViewModel } from 'view_models/proofread_approver';
import { ProofreadEventViewModel } from 'view_models/proofread_event';

const results = [
  { label: '取り下げ', value: 'canceled' },
  { label: '不合格', value: 'ng' },
  { label: '合格', value: 'ok' },
];

type AttachedResultEventFormDialogProps = {
  modalSet: ModalSet<ProofreadEvent>;
  onComplete: (event: ProofreadEvent) => void;
};
export const AttachedResultEventFormDialog: React.FC<AttachedResultEventFormDialogProps> = (props) => {
  const [eventVm, setEventVm] = useState(new ProofreadEventViewModel(props.modalSet.item));
  const globalState = useContext(GlobalStateContext);
  const mypageState = useContext(MypageStateContext);

  const patchForm = useForm<ProofreadEventForm>({
    ...props.modalSet.item,
    aasmState: 'fix_result',
    result: props.modalSet.item.result || 'ok',
    scheduledDate: props.modalSet.item.scheduledDate || moment().toString(),
    proofreadInfoAttributes: {
      ...props.modalSet.item.instrument?.proofreadInfo,
    },
  });
  const patchApi = useUpdateProofreadEventApi(patchForm);

  const handleSubmit = () => {
    globalState.confirm('校正結果を登録します。よろしいですか？', (e) => {
      if (e === 'ok') {
        patchApi.execute();
      }
    });
  };

  useEffectSkipFirst(() => {
    setEventVm(new ProofreadEventViewModel(props.modalSet.item));
    patchForm.resetForm();
  }, [props.modalSet.item.id]);

  useEffectSkipFirst(() => {
    if (!patchApi.isSuccess()) return;
    props.onComplete(patchApi.response.proofreadEvent);
    mypageState.reloadUser();
  }, [patchApi.loading]);

  return (
    <Modal
      modalId="event-planning-form"
      modalState={props.modalSet.modalState}
      title="校正計画実施結果"
      subTitle={eventVm.getInstrumentName()}
      size="sm"
      footer={
        <>
          <ThemedButton onClick={props.modalSet.closeModal} color="secondary">
            閉じる
          </ThemedButton>
          <ThemedButton
            color="primary"
            onClick={handleSubmit}
            disabled={
              props.modalSet.item.repairState === 'unnecessary'
                ? patchForm.object.result === 'ng' && !patchForm.object.approveNoRepair
                : !patchForm.object.approveUneditable
            }
          >
            登録
          </ThemedButton>
        </>
      }
    >
      {props.modalSet.item.suppliableType === 'Dealer' && (
        <div>
          <div>
            <label>パートナーメモ</label>
            <p style={{ paddingLeft: 12, margin: '6px 0' }}>{props.modalSet.item.noteOnDealerResult || 'なし'}</p>
          </div>
          <DisplayAttachmentsContainer
            label="パートナー添付ファイル"
            attachments={props.modalSet.item.attachments}
            kind="partner"
          />
        </div>
      )}
      <div>
        <label>結果</label>
        <SelectField
          form={patchForm}
          attr="result"
          apiError={patchApi.apiError}
          required
          data={results}
          labelId="result-id"
        />
      </div>
      <div>
        <label>添付ファイル</label>
        <FilesInput form={patchForm} attr="attachmentsAttributes" apiError={patchApi.apiError} />
      </div>

      {/* SelectField の minWidth デフォルト(200px)が他フィールドを圧迫して校正月が見切れるため、明示幅で配分する */}
      <Flex justifyContent="space-between" style={{ marginTop: 12 }}>
        <MonthField
          label="校正月"
          form={patchForm}
          attr="scheduledDate"
          required
          apiError={patchApi.apiError}
          width={190}
        />
        <NumberField
          label="校正周期"
          form={patchForm}
          attr={['proofreadInfoAttributes', 'cycle']}
          apiError={patchApi.apiError}
          width={140}
        />
        <SelectField
          form={patchForm}
          labelId="proofread-unit-label"
          attr={['proofreadInfoAttributes', 'cycleUnit']}
          label="周期単位"
          data={[
            { value: 'month', label: '月毎' },
            { value: 'year', label: '年毎' },
          ]}
          apiError={patchApi.apiError}
          width={150}
        />
      </Flex>

      {(props.modalSet.item.approvers || []).some((a) => a.kind === 'result' && a.aasmState === 'rejected') && (
        <div style={{ marginTop: 12 }}>
          <label>承認結果</label>
          <Table>
            <TableBody>
              {props.modalSet.item.approvers?.map((a) => {
                if (a.kind === 'result') {
                  const aVm = new ProofreadApproverViewModel(a);
                  return (
                    <TableRow key={`approve-${a.id}`}>
                      <ExTableCell style={{ width: 130 }}>
                        <div>
                          {a.approver?.familyName} {a.approver?.givenName}
                        </div>
                      </ExTableCell>
                      <ExTableCell style={{ width: 80 }}>
                        <div>{aVm.getApproveLabel()}</div>
                      </ExTableCell>
                      <ExTableCell>
                        <div>{aVm.getApprovalNote()}</div>
                      </ExTableCell>
                    </TableRow>
                  );
                }
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <StringField
        label="結果入力時メモ"
        form={patchForm}
        attr="noteOnResult"
        apiError={patchApi.apiError}
        multiline
        style={{ marginTop: 12 }}
      />
      <Collapse in={props.modalSet.item.repairState === 'unnecessary' && patchForm.object.result === 'ng'}>
        <CheckBoxField
          form={patchForm}
          attr="approveNoRepair"
          label="修理せずに校正結果を不合格にする"
          labelStyle={{ fontSize: 14 }}
        />
        <p style={{ color: 'red', fontSize: 12 }}>修理履歴を元に校正を進める場合は、不具合報告を作成してください</p>
      </Collapse>
      {props.modalSet.item.repairState !== 'unnecessary' && (
        <>
          <CheckBoxField
            form={patchForm}
            attr="approveUneditable"
            label="修理履歴を確定する"
            labelStyle={{ fontSize: 14 }}
          />
          <p style={{ color: 'red', fontSize: 12 }}>校正完了後は、この校正に紐づく修理履歴が編集できなくなります</p>
        </>
      )}
    </Modal>
  );
};

export default AttachedResultEventFormDialog;
